/**
 * The page a subcontractor's packet link opens (ADR 0279).
 *
 * `/sub/<token>`: no session, no row level security to satisfy, nothing of
 * ours in the bundle. Everything comes from the `crew-packet` Edge Function,
 * which also checks every box — this page checks only that the signature box
 * is ticked and a name is typed, and shows the function's messages beside the
 * boxes they are about, in the language the page is in.
 *
 * One long form rather than steps: a subcontractor filling it in on a phone
 * on a roof is more likely to scroll than to find a Back button, and the
 * agreement is long enough already.
 *
 * Nothing they type is kept in the browser. The form holds a Social Security
 * number and a bank account; a draft in local storage would leave them on
 * whatever phone it was filled in on.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { WORDMARK_ON_LIGHT } from "../../logo";
import type { Answers, PacketForm } from "./fields";
import {
  AgreementSection,
  BankSection,
  InsuranceSection,
  W9Section,
  type OpenPacket,
} from "./sections";
import { WORDS, type PacketLanguage } from "./words";

/** `/sub/<32 hex characters>` and nothing else. */
export function crewPacketTokenFromPath(pathname: string): string | null {
  return /^\/sub\/([0-9a-f]{32})\/?$/.exec(pathname)?.[1] ?? null;
}

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crew-packet`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

type Loaded =
  | { state: "loading" }
  | { state: "open"; packet: OpenPacket }
  | { state: "done"; filename: string; pdf: string; packet: OpenPacket }
  | { state: "closed"; message: string }
  | { state: "error" };

/** Today, as the subcontractor's own device reckons it. */
function todayLocal(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

interface Answer {
  ok: boolean;
  body: {
    state?: string;
    language?: string;
    message?: string;
    error?: string;
    errors?: Record<string, string>;
    filename?: string;
    pdf?: string;
  } & Partial<OpenPacket>;
}

/** Every call is a POST: the token stays out of logs and history. */
async function callPacket(
  token: string,
  action: "open" | "sign",
  payload: object,
): Promise<Answer> {
  const response = await fetch(FUNCTION_URL, {
    method: "POST",
    headers: {
      apikey: ANON_KEY,
      Authorization: `Bearer ${ANON_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token, action, ...payload }),
  });
  return { ok: response.ok, body: await response.json() };
}

/** What the opening answer means for the page. */
function loadedFrom(answer: Answer): Loaded {
  if (answer.ok && answer.body.state === "open") {
    return { state: "open", packet: answer.body as OpenPacket };
  }
  if (answer.body.message) {
    return { state: "closed", message: answer.body.message };
  }
  return { state: "error" };
}

/**
 * What the office already knows, so they type less. Never a number.
 *
 * Never the business name either. The crew's name is the office's own label
 * for them, often a nickname, and whatever is in that box is printed on line 2
 * of the W-9 and names the subcontractor in the agreement. Left blank, as the
 * IRS form means it, the agreement names their legal name unless they type a
 * business name of their own (issue #867).
 */
function startingAnswers(packet: OpenPacket): Answers {
  return {
    businessName: "",
    signerName: packet.crew.contactName ?? "",
    email: packet.crew.email ?? "",
    phone: packet.crew.phone ?? "",
    state: "AL",
  };
}

export function CrewPacketPage({ token }: { token: string }) {
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });
  const [language, setLanguage] = useState<PacketLanguage>("en");
  const words = WORDS[language];

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const answer = await callPacket(token, "open", { today: todayLocal() });
        if (cancelled) return;
        const sentIn = answer.body.language;
        if (sentIn === "es" || sentIn === "en") setLanguage(sentIn);
        setLoaded(loadedFrom(answer));
      } catch {
        if (!cancelled) setLoaded({ state: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const header = (
    <header className="ilink-head">
      <img src={WORDMARK_ON_LIGHT} alt="" />
      <div
        className="ilink-chips cpk-lang"
        role="group"
        aria-label={words.language}
      >
        {(["en", "es"] as const).map((code) => (
          <button
            key={code}
            type="button"
            className="ilink-chip"
            aria-pressed={language === code}
            lang={code}
            onClick={() => setLanguage(code)}
          >
            {code === "en" ? "English" : "Español"}
          </button>
        ))}
      </div>
    </header>
  );

  return (
    <main className="ilink" lang={language}>
      <div
        className={`ilink-card${loaded.state === "open" ? " cpk-card" : ""}`}
      >
        {header}
        {loaded.state === "open" ? (
          <PacketFormView
            token={token}
            packet={loaded.packet}
            language={language}
            onLanguage={setLanguage}
            onDone={setLoaded}
          />
        ) : (
          <PacketMessage loaded={loaded} language={language} />
        )}
      </div>
    </main>
  );
}

/** Everything but the form: loading, a dead link, and the thank-you. */
function PacketMessage({
  loaded,
  language,
}: {
  loaded: Exclude<Loaded, { state: "open" }>;
  language: PacketLanguage;
}) {
  const words = WORDS[language];
  if (loaded.state === "loading" || loaded.state === "error") {
    return (
      <p className="ilink-sub">
        {loaded.state === "loading" ? words.loading : words.unreachable}
      </p>
    );
  }
  if (loaded.state === "closed") {
    return (
      <div className="ilink-done">
        <p>{loaded.message}</p>
      </div>
    );
  }
  return (
    <div className="ilink-done">
      <div className="ilink-tick" aria-hidden="true">
        ✓
      </div>
      <h1>{words.doneTitle}</h1>
      <p>{words.done(loaded.packet.company)}</p>
      <a
        className="ilink-go cpk-download"
        href={`data:application/pdf;base64,${loaded.pdf}`}
        download={loaded.filename}
      >
        {words.download}
      </a>
      <p>{words.doneCertificate(loaded.packet.certificate.email)}</p>
    </div>
  );
}

/** What a signing answer means: done, closed, boxes to fix, or a sentence. */
function signingOutcome(
  answer: Answer,
  packet: OpenPacket,
):
  | { next: Loaded }
  | { errors: Record<string, string> }
  | { problem: string | null } {
  const { body } = answer;
  if (answer.ok && body.state === "signed") {
    return {
      next: {
        state: "done",
        filename: body.filename ?? "packet.pdf",
        pdf: body.pdf ?? "",
        packet,
      },
    };
  }
  if (body.errors) return { errors: body.errors };
  if (body.message && (body.state === "signed" || body.state === "closed")) {
    return { next: { state: "closed", message: body.message } };
  }
  return { problem: body.error ?? null };
}

function PacketFormView({
  token,
  packet,
  language,
  onLanguage,
  onDone,
}: {
  token: string;
  packet: OpenPacket;
  language: PacketLanguage;
  onLanguage: (language: PacketLanguage) => void;
  onDone: (loaded: Loaded) => void;
}) {
  const words = WORDS[language];
  const [answers, setAnswers] = useState<Answers>(() =>
    startingAnswers(packet),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const set = useCallback((field: string, value: string | boolean) => {
    setAnswers((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);
  const form: PacketForm = { answers, errors, words, set };

  const signerName = answers.signerName;
  const canSign =
    answers.consented === true &&
    typeof signerName === "string" &&
    signerName.trim().length >= 2;

  async function sign() {
    setSigning(true);
    setProblem(null);
    try {
      const outcome = signingOutcome(
        await callPacket(token, "sign", {
          ...answers,
          language,
          signedDate: todayLocal(),
        }),
        packet,
      );
      if ("next" in outcome) {
        onDone(outcome.next);
        window.scrollTo(0, 0);
      } else if ("errors" in outcome) {
        setErrors(outcome.errors);
        setProblem(words.fix);
        // To the first box that needs fixing, in the order they appear.
        requestAnimationFrame(() => {
          const first = formRef.current?.querySelector('[aria-invalid="true"]');
          if (first instanceof HTMLElement) first.focus();
        });
      } else {
        setProblem(outcome.problem ?? words.unreachable);
      }
    } catch {
      setProblem(words.unreachable);
    } finally {
      setSigning(false);
    }
  }

  return (
    <form
      className="ilink-form"
      ref={formRef}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (canSign && !signing) void sign();
      }}
    >
      <h1>{words.title}</h1>
      <p className="ilink-sub">{words.intro(packet.company)}</p>
      <W9Section form={form} language={language} />
      <BankSection form={form} />
      <InsuranceSection form={form} packet={packet} />
      <AgreementSection
        form={form}
        packet={packet}
        language={language}
        onLanguage={onLanguage}
      />
      {problem ? (
        <p className="ilink-problem" role="alert">
          {problem}
        </p>
      ) : null}
      <button className="ilink-go" type="submit" disabled={!canSign || signing}>
        {signing ? words.signing : words.sign}
      </button>
    </form>
  );
}
